import { useState, useEffect, useMemo } from 'react';
import {
  App,
  Card,
  Select,
  Input,
  Button,
  Form,
  Space,
  Tag,
  Result,
  Alert,
  Descriptions,
  Typography,
  Table,
  Tooltip,
} from 'antd';
import {
  ExperimentOutlined,
  SendOutlined,
  LineChartOutlined,
  SearchOutlined,
  ReloadOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { NotificationType, NotificationChannel, NotificationCategoryType } from '@mygate/common';
import { notificationsService, LabTriggerResponse } from '@/services/notifications';
import { api } from '@/services/api';
import { formatDateTime } from '@/utils/date';
import type { ApiResponse } from '@mygate/common';

const { Text } = Typography;

interface UserSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email?: string;
}

const CHANNEL_OPTIONS = [
  { value: '', label: 'Default (use fallback chain)' },
  { value: NotificationChannel.PUSH, label: 'Push' },
  { value: NotificationChannel.EMAIL, label: 'Email' },
  { value: NotificationChannel.SMS, label: 'SMS' },
  { value: NotificationChannel.WHATSAPP, label: 'WhatsApp' },
];

const categoryColors: Record<string, string> = {
  [NotificationCategoryType.TRANSACTIONAL]: 'blue',
  [NotificationCategoryType.PROMOTIONAL]: 'green',
  [NotificationCategoryType.INFORMATIONAL]: 'orange',
};

function getGrafanaLabLogsUrl(userId: string) {
  const baseUrl = 'https://grafana.mygateapp.de/explore';
  const panes = {
    d4t: {
      datasource: 'P8E80F9AEF21F6940',
      queries: [
        {
          refId: 'A',
          expr: `{compose_service="backend"} |= \`NOTIFICATION_LAB\` |= \`${userId}\``,
          queryType: 'range',
          datasource: { type: 'loki', uid: 'P8E80F9AEF21F6940' },
          editorMode: 'builder',
          direction: 'forward',
        },
      ],
      range: { from: 'now-1h', to: 'now' },
    },
  };

  return `${baseUrl}?schemaVersion=1&panes=${encodeURIComponent(JSON.stringify(panes))}&orgId=1`;
}

// Keep delivery diagnostics in the standard backoffice table layout.
export function NotificationLaboratory() {
  const { modal, message } = App.useApp();
  const [form] = Form.useForm();
  const [seeding, setSeeding] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState<Partial<NotificationType>[]>([]);
  const [selectedType, setSelectedType] = useState<Partial<NotificationType> | null>(null);
  const [userSearch] = useState('');
  const [userResults, setUserResults] = useState<UserSearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [result, setResult] = useState<LabTriggerResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [eventLogs, setEventLogs] = useState<
    Array<{
      id?: string;
      finalStatus?: string;
      finalChannel?: string;
      channelAttempts?: Array<{ channel: string; status: string; error?: string; deliveredAt?: string }>;
      error?: string;
      createdAt?: Date;
    }>
  >([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    loadTypes();
  }, []);

  async function loadTypes() {
    try {
      const response = await notificationsService.getNotificationTypes();
      if (response.success && response.data) {
        setNotificationTypes(response.data.types);
      }
    } catch {
      // Silently fail — types list will be empty
    }
  }

  function handleSeedTypes() {
    modal.confirm({
      title: 'Reset Notification Types',
      content:
        'This will reset all notification types to their default seed definitions. ' +
        'Custom template edits and channel changes will be overwritten. Continue?',
      okText: 'Reset',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setSeeding(true);
        try {
          const response = await notificationsService.seedNotificationTypes();
          if (response.success && response.data) {
            const { created, updated, total } = response.data;
            message.success(`Seed complete: ${created} created, ${updated} updated (${total} total)`);
            await loadTypes();
          } else {
            message.error('Failed to seed notification types');
          }
        } catch {
          message.error('Failed to seed notification types');
        } finally {
          setSeeding(false);
        }
      },
    });
  }

  async function fetchEventLogs(eventId: string) {
    setLogsLoading(true);
    try {
      const response = await notificationsService.getLogs({ eventId, limit: 10 });
      if (response.success && response.data) {
        setEventLogs(response.data.logs as typeof eventLogs);
      }
    } catch {
      // Silently fail
    } finally {
      setLogsLoading(false);
    }
  }

  async function searchUsers(query: string) {
    if (!query || query.length < 2) {
      setUserResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = (await api.get(`/api/staff/users?search=${encodeURIComponent(query)}&limit=10`)) as ApiResponse<{
        items: UserSearchResult[];
      }>;
      if (response.success && response.data) {
        setUserResults(response.data.items);
      }
    } catch {
      setUserResults([]);
    } finally {
      setSearching(false);
    }
  }

  function handleTypeSelect(typeCode: string) {
    const type = notificationTypes.find(t => t.code === typeCode) || null;
    setSelectedType(type);
    setResult(null);
    setError(null);

    // Reset placeholder fields when type changes
    if (type?.placeholders) {
      const placeholderDefaults: Record<string, string> = {};
      for (const p of type.placeholders) {
        placeholderDefaults[`ph_${p.key}`] = p.defaultValue || '';
      }
      form.setFieldsValue(placeholderDefaults);
    }
  }

  function handleUserSelect(userId: string) {
    const user = userResults.find(u => u.id === userId) || null;
    setSelectedUser(user);
    setResult(null);
    setError(null);
  }

  async function handleTrigger() {
    if (!selectedUser || !selectedType) return;

    setTriggering(true);
    setResult(null);
    setError(null);
    setEventLogs([]);

    try {
      const values = form.getFieldsValue();

      // Collect placeholders
      const placeholders: Record<string, string> = {};
      for (const p of selectedType.placeholders || []) {
        const val = values[`ph_${p.key}`];
        if (val) {
          placeholders[p.key] = val;
        }
      }

      const channelOverride = values.channelOverride || undefined;

      const response = await notificationsService.labTrigger({
        userId: selectedUser.id,
        eventType: selectedType.code!,
        placeholders: Object.keys(placeholders).length > 0 ? placeholders : undefined,
        channelOverride,
      });

      if (response.success && response.data) {
        setResult(response.data);
      } else {
        setError('Failed to trigger notification');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to trigger notification';
      setError(errorMessage);
    } finally {
      setTriggering(false);
    }
  }

  const typeOptions = useMemo(
    () =>
      notificationTypes.map(t => ({
        value: t.code,
        label: (
          <Space>
            <span>{t.name}</span>
            <Tag color={categoryColors[t.category || ''] || 'default'} style={{ fontSize: 10 }}>
              {t.category}
            </Tag>
            {!t.isEnabled && (
              <Tag color="red" style={{ fontSize: 10 }}>
                DISABLED
              </Tag>
            )}
          </Space>
        ),
      })),
    [notificationTypes],
  );

  const userOptions = useMemo(
    () =>
      userResults.map(u => ({
        value: u.id,
        label: `${u.firstName || ''} ${u.lastName || ''} (${u.phoneNumber})${u.email ? ` — ${u.email}` : ''}`,
      })),
    [userResults],
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Alert
        message="Notification Laboratory"
        description="Test notifications by triggering them for any user, including disabled types. All triggers are logged with [NOTIFICATION_LAB] prefix in Grafana."
        type="info"
        showIcon
        icon={<ExperimentOutlined />}
        action={
          <Button danger icon={<DatabaseOutlined />} loading={seeding} onClick={handleSeedTypes}>
            Reset Notification Types
          </Button>
        }
      />

      <Card title="Trigger Notification">
        <Form form={form} layout="vertical" onFinish={handleTrigger}>
          {/* User Picker */}
          <Form.Item label="User" required>
            <Select
              showSearch
              placeholder="Search by phone, name, or ID..."
              filterOption={false}
              onSearch={searchUsers}
              onChange={handleUserSelect}
              loading={searching}
              options={userOptions}
              notFoundContent={userSearch.length < 2 ? 'Type at least 2 characters' : 'No users found'}
              suffixIcon={<SearchOutlined />}
              value={selectedUser?.id}
              allowClear
              onClear={() => {
                setSelectedUser(null);
                setUserResults([]);
              }}
            />
          </Form.Item>

          {/* Notification Type */}
          <Form.Item label="Notification Type" required>
            <Select
              placeholder="Select notification type..."
              options={typeOptions}
              onChange={handleTypeSelect}
              value={selectedType?.code}
              showSearch
              filterOption={(input, option) => {
                const type = notificationTypes.find(t => t.code === option?.value);
                return (
                  (type?.name?.toLowerCase().includes(input.toLowerCase()) ?? false) ||
                  (type?.code?.toLowerCase().includes(input.toLowerCase()) ?? false)
                );
              }}
            />
          </Form.Item>

          {/* Placeholders */}
          {selectedType?.placeholders && selectedType.placeholders.length > 0 && (
            <Card size="small" title="Placeholders" style={{ marginBottom: 16, background: '#fafafa' }}>
              {selectedType.placeholders.map(p => (
                <Form.Item
                  key={p.key}
                  name={`ph_${p.key}`}
                  label={
                    <Space>
                      <Text code>{`{${p.key}}`}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {p.description}
                      </Text>
                      {p.required ? (
                        <Tag color="red" style={{ fontSize: 10 }}>
                          required
                        </Tag>
                      ) : (
                        <Tag style={{ fontSize: 10 }}>optional</Tag>
                      )}
                    </Space>
                  }
                  rules={p.required ? [{ required: true, message: `${p.key} is required` }] : undefined}
                  initialValue={p.defaultValue || ''}
                >
                  <Input placeholder={p.defaultValue || `Enter ${p.key}`} />
                </Form.Item>
              ))}
            </Card>
          )}

          {/* Channel Override */}
          <Form.Item name="channelOverride" label="Channel Override">
            <Select options={CHANNEL_OPTIONS} defaultValue="" />
          </Form.Item>

          {/* Actions */}
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                loading={triggering}
                disabled={!selectedUser || !selectedType}
              >
                Trigger Notification
              </Button>

              {selectedUser && (
                <Button
                  icon={<LineChartOutlined />}
                  onClick={() => window.open(getGrafanaLabLogsUrl(selectedUser.id), '_blank')}
                >
                  View Logs in Grafana
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Result */}
      {result && (
        <Card>
          <Result
            status="success"
            title="Notification Triggered"
            subTitle={`Event created for ${selectedType?.name}`}
            extra={
              <Space direction="vertical" style={{ width: '100%' }}>
                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item label="Event ID">
                    <Text copyable>{result.eventId}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Event Type">{result.eventType}</Descriptions.Item>
                  <Descriptions.Item label="User ID">
                    <Text copyable>{result.userId}</Text>
                  </Descriptions.Item>
                </Descriptions>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => fetchEventLogs(result.eventId)}
                    loading={logsLoading}
                  >
                    Refresh Delivery Logs
                  </Button>
                  <Button
                    type="link"
                    icon={<LineChartOutlined />}
                    onClick={() => window.open(getGrafanaLabLogsUrl(result.userId), '_blank')}
                  >
                    View in Grafana Logs
                  </Button>
                </Space>

                {eventLogs.length > 0 ? (
                  <Table
                    scroll={{ x: 800 }}
                    size="small"
                    dataSource={eventLogs}
                    rowKey="id"
                    pagination={false}
                    columns={[
                      {
                        title: 'Status',
                        dataIndex: 'finalStatus',
                        key: 'finalStatus',
                        width: 100,
                        render: (status: string) => {
                          const colors: Record<string, string> = {
                            DELIVERED: 'green',
                            FAILED: 'red',
                            CANCELLED: 'orange',
                            OPTED_OUT: 'default',
                          };
                          return <Tag color={colors[status] || 'default'}>{status}</Tag>;
                        },
                      },
                      {
                        title: 'Channel',
                        dataIndex: 'finalChannel',
                        key: 'finalChannel',
                        width: 100,
                        render: (channel: string) => {
                          const colors: Record<string, string> = {
                            [NotificationChannel.PUSH]: 'purple',
                            [NotificationChannel.EMAIL]: 'cyan',
                            [NotificationChannel.SMS]: 'orange',
                            [NotificationChannel.WHATSAPP]: 'green',
                          };
                          return <Tag color={colors[channel] || 'default'}>{channel || 'N/A'}</Tag>;
                        },
                      },
                      {
                        title: 'Created',
                        dataIndex: 'createdAt',
                        key: 'createdAt',
                        width: 160,
                        render: (date: string) => (date ? formatDateTime(new Date(date).toISOString()) : '-'),
                      },
                      {
                        title: 'Delivered',
                        key: 'deliveredAt',
                        width: 160,
                        render: (_: unknown, record: (typeof eventLogs)[0]) => {
                          const success = record.channelAttempts?.find(a => a.status === 'SUCCESS');
                          return success?.deliveredAt
                            ? formatDateTime(new Date(success.deliveredAt).toISOString())
                            : '-';
                        },
                      },
                      {
                        title: 'Error',
                        key: 'error',
                        render: (_: unknown, record: (typeof eventLogs)[0]) => {
                          const failedAttempts = record.channelAttempts?.filter(a => a.status === 'FAILED') || [];
                          const lastFailed = failedAttempts[failedAttempts.length - 1];
                          const errorMsg = lastFailed?.error || record.error;
                          return errorMsg ? (
                            <Tooltip title={errorMsg}>
                              <Text type="danger">{errorMsg}</Text>
                            </Tooltip>
                          ) : (
                            '-'
                          );
                        },
                      },
                    ]}
                  />
                ) : (
                  <Alert
                    type="info"
                    message="Notification not yet processed. Click Refresh to check delivery status."
                    showIcon
                  />
                )}
              </Space>
            }
          />
        </Card>
      )}

      {/* Error */}
      {error && (
        <Alert
          message="Trigger Failed"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}
    </Space>
  );
}
